import { fbEnrichUsersFromIds } from './../../../data/FirebaseEnrich'
import { useToasts } from '@geist-ui/react'
import { useQuery, useSubscription } from '@apollo/client'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { rwBuySellDialogAtom, SECONDARY_ACTION } from '../../../atoms/SecondaryDialog'
import {
  indexerClient,
  LISTINGS_QUERY,
  LISTING_STATUS_QUERY,
  OFFERS_QUERY,
  OFFER_STATUS_QUERY,
  SECONDARY_USER_SUBSCRIPTION,
} from '../../../graphql/graphql-queries'
import { useWallet } from '@solana/wallet-adapter-react'
import useAuth from '../../../hooks/auth'
import { useFirebaseAuthGuard } from '../../../hooks/firebase'
import { useTxToast } from '../../../hooks/toast'
import { network } from '../../../metadata/2021-12-22/primitives/network'
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  sendAndConfirmRawTransaction,
  Transaction,
} from '@solana/web3.js'
import { TokenProgram } from '../../../program/TokenProgram'
import { Query_Root } from '../../../graphql/indexer-graphql-types'
// import { txSigner } from '../../../data/TxSigner'
// import { Base64 } from 'js-base64'

const useSecondaryDialogState = () => {
  const [secondaryAction, setSecondaryAction] = useAtom(rwBuySellDialogAtom)
  const [price, setPrice] = useState(0)

  const isDialogOpen = secondaryAction.type !== SECONDARY_ACTION.NONE

  //! TRANSFER OF VARUN'S LOGIC

  const { provider } = useAuth()
  const wallet = useWallet()
  const [, setToast] = useToasts()

  const secondaryData = useSubscription<Query_Root>(SECONDARY_USER_SUBSCRIPTION, {
    variables: {
      mint: secondaryAction.video?.address,
      user: wallet.publicKey?.toString() ?? '',
    },
  }).data as any

  const listings = useQuery<Query_Root>(LISTINGS_QUERY, {
    variables: {
      mint: secondaryAction.video?.address,
      offset: 0,
      limit: 1,
    },
  })

  const offers = useQuery<Query_Root>(OFFERS_QUERY, {
    variables: {
      mint: secondaryAction.video?.address,
      offset: 0,
      limit: 1,
    },
  })

  const mintObject = secondaryData?.mint[0]
  const metadataObject = mintObject?.editionMetadata?.metadataObject

  //TODO: Incorporate showing live data for cart item
  const secondaryInfo = useMemo(() => {
    return metadataObject &&
      metadataObject.purchaseReceiptsAggregate?.aggregate?.sum !== undefined &&
      metadataObject.purchaseReceiptsAggregate?.aggregate?.sum !== null &&
      mintObject.listingsWithTokenAggregate?.aggregate?.min !== undefined &&
      mintObject.listingsWithTokenAggregate?.aggregate?.min !== null &&
      metadataObject.bidReceiptsAggregate?.aggregate?.max !== undefined &&
      metadataObject.bidReceiptsAggregate?.aggregate?.max !== null
      ? {
          floor_price: mintObject.listingsWithTokenAggregate?.aggregate?.min.price || 0,
          highest_bid: metadataObject.bidReceiptsAggregate?.aggregate?.max.price || 0,
          volume: metadataObject.purchaseReceiptsAggregate?.aggregate?.sum.price || 0,
        }
      : undefined
  }, [metadataObject])

  const creatorFeePercent =
    (secondaryData?.mint[0].editionsMetadata?.creatorFeeBasisPoints ?? 0) / 100

  const platformFeePercent =
    (secondaryData?.mint[0].editionMetadata?.platformFeeBasisPoints ?? 0) / 100

  const highestOfferPrice = useMemo(
    () => Math.round((secondaryInfo?.highest_bid / LAMPORTS_PER_SOL) * 100) / 100,
    [secondaryInfo?.highest_bid]
  )
  const floorPrice = useMemo(
    () => Math.round((secondaryInfo?.floor_price / LAMPORTS_PER_SOL) * 100) / 100,
    [secondaryInfo?.floor_price]
  )

  // const ownedAmount = secondaryData?.mint[0].tokenAccountsAggregate.aggregate?.sum?.amount ?? 0

  const showTxToast = useTxToast()

  const [, showToast] = useToasts()

  //* Toast related stuff

  const processingToast = useCallback(
    async (text: string): Promise<void> => {
      setSecondaryAction({ type: SECONDARY_ACTION.NONE })
      showToast({
        text,
        type: 'warning',
      })
    },
    [setSecondaryAction, showToast]
  )

  const confirmationToast = useCallback(
    async (trans: string, text: string, action: SECONDARY_ACTION): Promise<void> => {
      setSecondaryAction({ type: SECONDARY_ACTION.NONE, refresh: action })
      showTxToast({
        nw:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
            ? network.SOL_MAINNET
            : network.SOL_DEVNET,
        hash: trans,
        text,
      })
      setSecondaryAction({ type: SECONDARY_ACTION.NONE })
    },
    [setSecondaryAction, showTxToast]
  )

  //* Action related stuff

  const listForSale = useCallback(
    async (price: number, executeAfter: boolean): Promise<Transaction | undefined> => {
      if (!secondaryAction.video || !provider || !wallet || !wallet.publicKey || !price) return

      const result: Query_Root = (
        await indexerClient.query({
          query: OFFER_STATUS_QUERY,
          fetchPolicy: 'no-cache',
          variables: {
            address: wallet.publicKey.toString(),
            mint: secondaryAction.video.address,
          },
        })
      ).data

      const count = result?.bidReceiptAggregate?.aggregate?.count

      if (count && count > 0) {
        setToast({
          text: !executeAfter
            ? `Sorry, at the moment you can't make a listing while you have offers. Cancel your offers first.`
            : "Sorry, at the moment you can't sell while you have offers. Cancel your offers first.",
          type: 'error',
        })
        return
      }

      const mint = new PublicKey(secondaryAction.video.address)
      const program = TokenProgram.getProgram(provider)
      const platformSigner =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('GmhU4c5iGto2p7JeKsan5nNi7qbCuqRbs6Qezy11nims')
          : await TokenProgram.getPlatformSignerAddress(program)
      const auctionHouse =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('G6zwimoXCxao9EN2vfCki6iqCUPtFcNXEun3b7GmV1cy')
          : new PublicKey('DN3C8AbvhRpvpzmmsLSVWmmG3TmPXiKSobcv7TQsJSy4')

      const [metadata] = await TokenProgram.findMetadataAddress(mint)

      const transaction = await TokenProgram.createSellOffer(
        provider.connection,
        wallet.publicKey,
        auctionHouse,
        platformSigner,
        mint,
        metadata,
        price,
        1
      )

      if (executeAfter) {
        return transaction
      } else {
        transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

        // const encoded = Base64.fromUint8Array(
        //   transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
        //   false
        // )
        // const adminSignedIx = await txSigner(encoded) // TODO: secondaryTxSigner

        // const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

        const signed = await provider.wallet.signTransaction(transaction)

        const serialized = signed.serialize()

        const fee = await transaction.getEstimatedFee(provider.connection)

        const walletBalance = await provider.connection.getBalance(wallet.publicKey)

        if (walletBalance < fee) {
          setToast({
            text: `Whoops, you don't have enough SOL!`,
            type: 'error',
          })
          return
        }

        processingToast('Listing your video for sale...')
        const hash = await sendAndConfirmRawTransaction(provider.connection, serialized, {
          commitment: 'confirmed',
          // skipPreflight: true,
          preflightCommitment: 'confirmed',
        })

        confirmationToast(hash, `Your video is listed for sale!`, SECONDARY_ACTION.SELL)
      }
    },
    [secondaryAction.video, provider, confirmationToast, wallet]
  )

  const [creatorName, setCreatorName] = useState<string>('')

  useEffect(() => {
    const creatorAddress = secondaryAction.video?.created_by

    if (creatorAddress) {
      fbEnrichUsersFromIds([creatorAddress]).then((enrichedUsers) => {
        setCreatorName(enrichedUsers[0].displayName)
      })
    }
  }, [secondaryAction.video?.created_by, secondaryAction.video])

  const makeOffer = useCallback(
    async (price: number, executeAfter: boolean): Promise<Transaction | undefined> => {
      if (!secondaryAction.video || !provider || !wallet || !wallet.publicKey) return

      if (!executeAfter) {
        const result: Query_Root = (
          await indexerClient.query({
            query: LISTING_STATUS_QUERY,
            fetchPolicy: 'no-cache',
            variables: {
              address: wallet.publicKey.toString(),
              mint: secondaryAction.video.address,
            },
          })
        ).data

        const tokenAcct =
          result.tokenAccount && result.tokenAccount.length > 0 ? result.tokenAccount[0] : undefined

        if (tokenAcct) {
          const delegate = tokenAcct.delegate ? new PublicKey(tokenAcct.delegate) : undefined

          if (delegate) {
            setToast({
              text: `Sorry, you can't make an offer while you have listings! Cancel your listings first.`,
              type: 'error',
            })
            return
          }
        }
      }

      const mint = new PublicKey(secondaryAction.video.address)
      const program = TokenProgram.getProgram(provider)
      const platformSigner =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('GmhU4c5iGto2p7JeKsan5nNi7qbCuqRbs6Qezy11nims')
          : await TokenProgram.getPlatformSignerAddress(program)
      const auctionHouse =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('G6zwimoXCxao9EN2vfCki6iqCUPtFcNXEun3b7GmV1cy')
          : new PublicKey('DN3C8AbvhRpvpzmmsLSVWmmG3TmPXiKSobcv7TQsJSy4')
      const [metadata] = await TokenProgram.findMetadataAddress(mint)

      const results = await program.provider.connection.getTokenLargestAccounts(mint)

      const transaction = await TokenProgram.createBuyOffer(
        provider.connection,
        wallet.publicKey,
        results.value[0].address,
        auctionHouse,
        platformSigner,
        mint,
        metadata,
        price,
        1,
        true
      )

      if (executeAfter) {
        return transaction
      } else {
        transaction.recentBlockhash = (await provider.connection.getLatestBlockhash()).blockhash

        // const encoded = Base64.fromUint8Array(
        //   transaction.serialize({ requireAllSignatures: false, verifySignatures: true }),
        //   false
        // )
        // const adminSignedIx = await txSigner(encoded) // TODO: secondaryTxSigner

        // const tx = Transaction.from(Base64.toUint8Array(adminSignedIx.tx_data))

        const signed = await provider.wallet.signTransaction(transaction)

        const serialized = signed.serialize()

        const fee = await transaction.getEstimatedFee(provider.connection)

        const walletBalance = await provider.connection.getBalance(wallet.publicKey)

        if (walletBalance < fee + price) {
          setToast({
            text: `Whoops, you don't have enough SOL!`,
            type: 'error',
          })
          return
        }

        processingToast('Making an offer...')
        const hash = await sendAndConfirmRawTransaction(provider.connection, serialized, {
          commitment: 'confirmed',
          preflightCommitment: 'confirmed',
        })

        confirmationToast(hash, `You made an offer for this NFT!`, SECONDARY_ACTION.BUY)
      }
    },
    [secondaryAction.video, provider, confirmationToast, wallet]
  )

  const executeSale = useCallback(
    async (
      transaction: Transaction,
      seller: PublicKey,
      buyer: PublicKey,
      price: number
    ): Promise<void> => {
      if (!secondaryAction.video || !provider || !wallet || !wallet.publicKey) return

      const mint = new PublicKey(secondaryAction.video.address)
      const program = TokenProgram.getProgram(provider)
      const platformSigner =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('GmhU4c5iGto2p7JeKsan5nNi7qbCuqRbs6Qezy11nims')
          : await TokenProgram.getPlatformSignerAddress(program)
      const auctionHouse =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? new PublicKey('G6zwimoXCxao9EN2vfCki6iqCUPtFcNXEun3b7GmV1cy')
          : new PublicKey('DN3C8AbvhRpvpzmmsLSVWmmG3TmPXiKSobcv7TQsJSy4')
      const [metadata] = await TokenProgram.findMetadataAddress(mint)

      const buffer = await TokenProgram.executeSale(
        provider,
        buyer,
        seller,
        auctionHouse,
        platformSigner,
        mint,
        metadata,
        price,
        1,
        true,
        wallet.publicKey,
        transaction
      )

      const fee = await transaction.getEstimatedFee(provider.connection)

      const walletBalance = await provider.connection.getBalance(wallet.publicKey)

      if (
        (seller.equals(wallet.publicKey) && walletBalance < fee) ||
        (buyer.equals(wallet.publicKey) && walletBalance < fee + price)
      ) {
        setToast({
          text: `Whoops, you don't have enough SOL!`,
          type: 'error',
        })
        return
      }

      processingToast(
        seller.equals(wallet.publicKey) ? 'Selling your video...' : 'Purchasing video...'
      )

      const hash = await sendAndConfirmRawTransaction(provider.connection, buffer, {
        commitment: 'confirmed',
        preflightCommitment: 'confirmed',
      })

      confirmationToast(
        hash,
        seller.equals(wallet.publicKey) ? 'Your video was sold!' : 'You purchased a video!',
        seller.equals(wallet.publicKey) ? SECONDARY_ACTION.BUY : SECONDARY_ACTION.SELL
      )
    },
    [secondaryAction.video, provider, confirmationToast, wallet]
  )

  const isAnyListingExisting = useMemo(() => {
    return secondaryInfo?.floor_price !== undefined && secondaryInfo.floor_price > 0
  }, [secondaryInfo?.floor_price])

  const isAnyOfferExisting = useMemo(() => {
    return secondaryInfo?.highest_bid !== undefined && secondaryInfo.highest_bid > 0
  }, [secondaryInfo?.highest_bid])

  const handleBuyNow = useCallback(async () => {
    if (!wallet?.publicKey) return

    const { data } = await listings.refetch()
    const listingsResults = data.listings
    if (
      !(
        (listingsResults || []).length > 0 &&
        listingsResults[0]?.price &&
        listingsResults[0]?.seller
      )
    )
      return
    const transaction = await makeOffer(
      listingsResults[0]?.price, //price
      true
    )
    if (!transaction) return
    await executeSale(
      transaction,
      new PublicKey(listingsResults[0]?.seller), //seller
      wallet.publicKey,
      listingsResults[0]?.price //price
    )
  }, [makeOffer, wallet, executeSale])

  const handleMakeOffer = useCallback(async () => {
    if (!wallet?.publicKey) return

    // It is not allowed to crete multiple offers with the same amount. This is
    // because of a AuctionHouse issue. Therefore, we add a random little number
    // to it to make it unique.
    const fuzz = Math.floor(Math.random() * (1000000 + 1))
    await makeOffer(price * LAMPORTS_PER_SOL + fuzz, false)
  }, [makeOffer, price])

  const handleSellNow = useCallback(async () => {
    if (!wallet?.publicKey) return

    const { data } = await offers.refetch()
    const refetchedMetadataObject = data.mint[0]?.editionMetadata?.metadataObject
    if (
      !(
        (refetchedMetadataObject?.bidReceipts || []).length > 0 &&
        refetchedMetadataObject?.bidReceipts[0]?.price &&
        refetchedMetadataObject?.bidReceipts[0]?.buyer
      )
    )
      return
    const transaction = await listForSale(
      refetchedMetadataObject?.bidReceipts[0]?.price, //price,
      true
    )
    if (!transaction) return
    await executeSale(
      transaction,
      wallet.publicKey,
      new PublicKey(refetchedMetadataObject?.bidReceipts[0]?.buyer), //buyer,
      refetchedMetadataObject?.bidReceipts[0]?.price //price
    )
  }, [listForSale, wallet, executeSale])

  const handleCreateListing = useCallback(async () => {
    if (!wallet?.publicKey) return

    const fuzz = Math.floor(Math.random() * (1000000 + 1))
    await listForSale(price * LAMPORTS_PER_SOL + fuzz, false)
  }, [listForSale, price])

  const nowAction = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY: {
        return handleBuyNow
      }
      case SECONDARY_ACTION.SELL: {
        return handleSellNow
      }
      default: {
        return () => {
          console.log('Sorry.')
        }
      }
    }
  }, [secondaryAction.type, handleBuyNow, handleSellNow])

  const laterAction = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY: {
        return handleMakeOffer
      }
      case SECONDARY_ACTION.SELL: {
        return handleCreateListing
      }
      default: {
        return () => {
          console.log('Sorry.')
        }
      }
    }
  }, [secondaryAction.type, handleMakeOffer, handleCreateListing])

  //? This is used in case the user isn't connected yet but want to perform an
  //? action.
  const guardedNowAction = useFirebaseAuthGuard(nowAction)
  const guardedLaterAction = useFirebaseAuthGuard(laterAction)

  //! END OF VARUN'S CODE

  //* UI related data

  const title = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY:
        return 'Buy From Secondary'
      case SECONDARY_ACTION.SELL:
        return 'Sell On Secondary'
      default:
        return ''
    }
  }, [secondaryAction.type])

  const [cartItemFloorPrice, setCartItemFloorPrice] = useState(0)
  const [highestOffer, setHighestOffer] = useState(0)

  useEffect(() => {
    if (floorPrice) {
      setCartItemFloorPrice(floorPrice)
    }
  }, [floorPrice])

  useEffect(() => {
    if (highestOfferPrice) {
      setHighestOffer(highestOfferPrice)
    }
  }, [highestOfferPrice])

  const cartItemDetails: CartItemDetails = useMemo(
    () => ({
      title: secondaryAction.video?.title || '',
      creatorName: creatorName,
      thumbnailUri: `https://glassimg.imgix.net/${secondaryAction.video?.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=1200&h=675`,
      floorPrice: cartItemFloorPrice,
    }),
    [creatorName, cartItemFloorPrice, secondaryAction.video]
  )

  const hintLabel = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY: {
        if (isAnyListingExisting && cartItemFloorPrice > 0) {
          const floorPriceDifference = ((price - cartItemFloorPrice) / cartItemFloorPrice) * 100

          if (floorPriceDifference > 0) {
            return `This is above the floor price - just buy the floor.`
          } else if (floorPriceDifference < 0) {
            return `You are ${Math.abs(floorPriceDifference).toFixed(1)}% below the floor price.`
          } else {
            return `This is the floor price.`
          }
        } else {
          return 'Make an offer below the floor price.'
        }
      }
      case SECONDARY_ACTION.SELL:
        return `You'll receive ${(price * 0.9).toFixed(1)} SOL.`
      default:
        return ''
    }
  }, [secondaryAction.type, price, cartItemFloorPrice])

  const hintInfos = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY:
        return `The floor price is the lowest price\nfor this NFT on the secondary market.`
      case SECONDARY_ACTION.SELL:
        return `  ${price.toFixed(2)} SOL\n- ${(price * 0.02).toFixed(
          2
        )} SOL (${platformFeePercent}% platform fee)\n- ${(price * 0.08).toFixed(
          2
        )} SOL (${creatorFeePercent}% royalty fee)\n= ${(price * 0.9).toFixed(2)} SOL`
      default:
        return ''
    }
  }, [secondaryAction.type, price, creatorFeePercent, platformFeePercent])

  const laterActionLabel = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY:
        return 'Make Offer'
      case SECONDARY_ACTION.SELL:
        return 'Create Listing'
      default:
        return ''
    }
  }, [secondaryAction.type])

  const nowActionLabel = useMemo(() => {
    switch (secondaryAction.type) {
      case SECONDARY_ACTION.BUY:
        return `Buy Now (${cartItemFloorPrice} SOL)`
      case SECONDARY_ACTION.SELL:
        return `Sell Now (${highestOffer} SOL)`
      default:
        return ''
    }
  }, [secondaryAction.type, highestOffer, cartItemFloorPrice])

  return {
    isDialogOpen,
    title,
    cartItemDetails,
    hintLabel,
    laterActionLabel,
    nowActionLabel,
    guardedNowAction,
    guardedLaterAction,
    price,
    setPrice,
    cartItemFloorPrice,
    hintInfos,
    secondaryAction,
    setSecondaryAction,
    isAnyListingExisting,
    isAnyOfferExisting,
  }
}

export default useSecondaryDialogState

export interface CartItemDetails {
  title: string
  creatorName: string
  thumbnailUri: string
}
